import { useState } from 'react';
import { Bot, Sparkles, ThumbsUp, ThumbsDown, ExternalLink, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { MarkdownRenderer } from './ChatWidget/MarkdownRenderer';
import { RagSource } from '@/services/api';

interface AISearchAnswerProps {
  answer: string;
  confidence: number;
  sources: RagSource[];
  onFeedback?: (helpful: boolean) => void;
}

export function AISearchAnswer({
  answer,
  confidence,
  sources,
  onFeedback,
}: AISearchAnswerProps) {
  const [feedbackGiven, setFeedbackGiven] = useState<boolean | null>(null);

  const handleFeedback = (helpful: boolean) => {
    setFeedbackGiven(helpful);
    onFeedback?.(helpful);
  };

  const confidencePercent = Math.round(confidence * 100);

  return (
    <div className="bg-gradient-to-r from-purple-50 via-blue-50 to-indigo-50 rounded-xl border border-purple-200 mb-6 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-white/50 border-b border-purple-100 dark:bg-gray-800">
        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center">
          <Bot className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">AI Answer</h3>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 rounded-full">
              <Sparkles className="h-3 w-3 text-purple-600" />
              <span className="text-xs font-medium text-purple-700">Powered by AI</span>
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Based on your knowledge base</p>
        </div>
        {confidence > 0 && (
          <div className="text-right">
            <div className="text-xs text-gray-500 dark:text-gray-400">Confidence</div>
            <div className={`text-sm font-semibold ${
              confidencePercent >= 70 ? 'text-green-600' :
              confidencePercent >= 40 ? 'text-yellow-600' : 'text-orange-600'
            }`}>
              {confidencePercent}%
            </div>
          </div>
        )}
      </div>

      {/* Answer Content */}
      <div className="p-4">
        <div className="bg-white rounded-lg p-4 shadow-sm dark:bg-gray-800">
          <MarkdownRenderer content={answer} className="text-gray-700 leading-relaxed dark:text-gray-200" />
        </div>
      </div>

      {/* Sources */}
      {sources.length > 0 && (
        <div className="px-4 pb-4">
          <h4 className="text-sm font-medium text-gray-600 mb-2 flex items-center gap-2 dark:text-gray-400">
            <FileText className="h-4 w-4" />
            Sources ({sources.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {sources.slice(0, 4).map((source, idx) => (
              <Link
                key={source.id}
                to={source.slug ? `/article/${source.slug}` : '#'}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-white rounded-full border border-gray-200 text-sm text-gray-700 hover:border-purple-300 hover:bg-purple-50 transition-colors dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700"
              >
                <span className="w-5 h-5 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-medium">
                  {idx + 1}
                </span>
                <span className="truncate max-w-[200px]">{source.title}</span>
                <ExternalLink className="h-3 w-3 text-gray-400 flex-shrink-0 dark:text-gray-500" />
              </Link>
            ))}
            {sources.length > 4 && (
              <span className="inline-flex items-center px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400">
                +{sources.length - 4} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Feedback */}
      <div className="px-4 py-3 bg-white/50 border-t border-purple-100 flex items-center justify-between dark:bg-gray-800">
        <span className="text-xs text-gray-500 dark:text-gray-400">Was this answer helpful?</span>
        <div className="flex items-center gap-2">
          {feedbackGiven === null ? (
            <>
              <button
                onClick={() => handleFeedback(true)}
                className="p-2 hover:bg-green-100 rounded-lg transition-colors group"
                aria-label="Yes, helpful"
              >
                <ThumbsUp className="h-4 w-4 text-gray-400 group-hover:text-green-600 dark:text-gray-500" />
              </button>
              <button
                onClick={() => handleFeedback(false)}
                className="p-2 hover:bg-red-100 rounded-lg transition-colors group"
                aria-label="No, not helpful"
              >
                <ThumbsDown className="h-4 w-4 text-gray-400 group-hover:text-red-600 dark:text-gray-500" />
              </button>
            </>
          ) : (
            <span className={`text-xs ${feedbackGiven ? 'text-green-600' : 'text-gray-500'} dark:text-gray-400`}>
              {feedbackGiven ? 'Thanks for your feedback!' : "Thanks, we'll improve!"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default AISearchAnswer;
